import type { ActionCableManagerAPI } from 'components/AI/ActionCableManager'
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
} from 'react'

/**
 * WebSocket Context Interface
 *
 * Replaces the singleton webSocketRegistry pattern with React Context
 * for better testability and React patterns.
 */
export interface WebSocketContextValue {
  /**
   * Register a WebSocket manager with the context
   */
  registerManager: (id: string, manager: ActionCableManagerAPI) => void

  /**
   * Unregister a WebSocket manager
   */
  unregisterManager: (id: string) => void

  /**
   * Get a specific manager by ID
   */
  getManager: (id: string) => ActionCableManagerAPI | undefined

  /**
   * Get the currently active manager
   */
  getActiveManager: () => ActionCableManagerAPI | undefined

  /**
   * Get the active manager ID
   */
  getActiveManagerId: () => string | null

  /**
   * Check if there's an active connected manager
   */
  hasActiveConnection: () => boolean

  /**
   * Get all registered manager IDs (for debugging)
   */
  getAllManagerIds: () => string[]
}

const WebSocketContext = createContext<WebSocketContextValue | undefined>(
  undefined
)

export interface WebSocketProviderProps {
  children: ReactNode
}

/**
 * WebSocket Context Provider
 *
 * Manages WebSocket manager instances using React Context instead of singleton.
 * Maintains the same single-connection enforcement as the original registry.
 */
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
}) => {
  const managersRef = useRef<Map<string, ActionCableManagerAPI>>(new Map())
  const activeManagerIdRef = useRef<string | null>(null)

  const registerManager = useCallback(
    (id: string, manager: ActionCableManagerAPI) => {
      const managers = managersRef.current
      const existingManager = managers.get(id)

      // If registering the same instance, do nothing
      if (existingManager === manager) {
        return
      }

      // If there's an existing manager for this ID, disconnect it
      if (existingManager) {
        existingManager.disconnect()
      }

      // If there's a different active manager, disconnect and remove it
      if (activeManagerIdRef.current && activeManagerIdRef.current !== id) {
        const activeManager = managers.get(activeManagerIdRef.current)
        if (activeManager) {
          activeManager.disconnect()
          managers.delete(activeManagerIdRef.current)
        }
      }

      managers.set(id, manager)
      activeManagerIdRef.current = id
    },
    []
  )

  const unregisterManager = useCallback((id: string) => {
    const managers = managersRef.current
    const manager = managers.get(id)
    if (manager) {
      manager.disconnect()
      managers.delete(id)

      if (activeManagerIdRef.current === id) {
        activeManagerIdRef.current = null
      }
    }
  }, [])

  const getManager = useCallback((id: string) => {
    return managersRef.current.get(id)
  }, [])

  const getActiveManager = useCallback(() => {
    if (!activeManagerIdRef.current) {
      return undefined
    }
    return managersRef.current.get(activeManagerIdRef.current)
  }, [])

  const getActiveManagerId = useCallback(() => {
    return activeManagerIdRef.current
  }, [])

  const hasActiveConnection = useCallback(() => {
    const activeManager = getActiveManager()
    return activeManager?.connected === true
  }, [getActiveManager])

  const getAllManagerIds = useCallback(() => {
    return Array.from(managersRef.current.keys())
  }, [])

  const contextValue: WebSocketContextValue = {
    registerManager,
    unregisterManager,
    getManager,
    getActiveManager,
    getActiveManagerId,
    hasActiveConnection,
    getAllManagerIds,
  }

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  )
}

/**
 * Hook to access WebSocket Context
 *
 * @throws Error if used outside WebSocketProvider
 */
export const useWebSocketContext = (): WebSocketContextValue => {
  const context = useContext(WebSocketContext)
  if (context === undefined) {
    throw new Error(
      'useWebSocketContext must be used within a WebSocketProvider'
    )
  }
  return context
}
